import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid2 as Grid,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { isEmpty } from 'lodash';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle, FilePlus01, Pencil02, RefreshCCW04, Trash01, UploadCloud01 } from 'untitledui-js-base';
import { useCommonApi } from '../api';
import { RENTAL_CAR_INFO_KEY } from '../api/constants';
import { onSuccessCustomApiResponse } from '../api/utils';
import { useNotify } from '../hooks';
import { PATHS } from '../paths';
import { formatPrice } from '../utils/formatPrice';
import { ConfirmRegenerateDocumentDialog } from './accidents/ConfirmRegenerateDocumentDialog';
import {
  BasicDocumentValueItem,
  BoxWithHeader,
  LineClamp,
  PageLayout,
  PageLayoutProps,
  RouterLink,
  TransparentChip
} from './common';
import { GenerateDocumentButton } from './common/GenerateDocumentButton';
import { SignatureDialog } from './common/SignatureDialog';
import { useAuth } from './hooks';
import { useRentalCar } from './hooks/useRentalCar';
import { RentFinishConfirmDialog } from './rental/RentFinishConfirmDialog';
import { RentRemoveConfirmDialog } from './rental/RentRemoveConfirmDialog';
import { RentStartConfirmDialog } from './rental/RentStartConfirmDialog';
import { RentalCarInfoDialog } from './rental/RentalCarInfoDialog';
import { Role } from './types';
import { RENT_FILES, RentCarUpdateRequest } from './types/rental';

interface RentalPageContentProps extends PageLayoutProps {
  rentalId: string;
  parentPath: string;
}

interface InfoRowProps {
  label: string;
  children?: React.ReactNode;
}

const InfoRow = ({ label, children }: InfoRowProps) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px 1fr' }, columnGap: 2, rowGap: 0.25 }}>
    <Typography color="text.secondary" fontWeight={500}>
      {label}
    </Typography>
    <Typography component="div" fontWeight={600}>
      {children}
    </Typography>
  </Box>
);

const RentalPageContent = ({ rentalId, parentPath, ...props }: RentalPageContentProps) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { notifyOnSuccess, notifyOnError } = useNotify();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [regenOpenTemplateName, setRegenOpenTemplateName] = useState<RENT_FILES | null>(null);

  const [selectedDocumentName, setSelectedDocumentName] = React.useState<RENT_FILES | null>(null);
  const [openDocFormDialogDocumentName, setOpenDocFormDialogDocumentName] = useState<RENT_FILES | null>(null);

  const { data: rentalCar, isPending: isLoadingData } = useQuery(
    useCommonApi().getRentalCar(Number.parseInt(rentalId))
  );

  const { data: rentalCarInfo } = useQuery({
    ...useCommonApi().getCarRentalsCarInfo(rentalCar?.car?.id as number),
    enabled: !!rentalCar
  });

  const {
    carRentalStatuses,
    carRentalBranches,
    rentRemoveDialogId,
    rentFinishDialogId,
    setRentRemoveDialogId,
    setRentFinishDialogId,
    isRemovingRent,
    isTakingoverRent,
    handleFinishRent,
    handleRemoveRent,
    updateRent,
    generateDocument,
    isGeneratingDocument,
    isStartingRent,
    openStartRentDialogId,
    handleStartRent,
    setOpenStartRentDialogId
  } = useRentalCar();

  const methods = useForm<RentCarUpdateRequest>({
    shouldUnregister: true,
    defaultValues: {
      rental: {
        rentalDate: rentalCar?.rentalDate ? parseISO(rentalCar.rentalDate as unknown as string) : new Date(),
        returnDate: rentalCar?.returnDate ? parseISO(rentalCar.returnDate as unknown as string) : new Date(),
        note: rentalCar?.note,
        dailyRate: rentalCar?.dailyRate
      }
    }
  });

  const { handleSubmit, control, reset } = methods;

  useEffect(() => {
    reset({
      rental: {
        rentalDate: rentalCar?.rentalDate ? parseISO(rentalCar.rentalDate as unknown as string) : new Date(),
        returnDate: rentalCar?.returnDate ? parseISO(rentalCar.returnDate as unknown as string) : new Date(),
        note: rentalCar?.note,
        dailyRate: rentalCar?.dailyRate
      }
    });
  }, [rentalCar]);

  const onSubmit = (formData: RentCarUpdateRequest) => {
    updateRent({ rentId: Number.parseInt(rentalId ?? ''), rentData: formData });
  };

  const handleGenerateDocument = (documentName: RENT_FILES, carParameters?: Record<string, any>) =>
    generateDocument(
      {
        rentalDocumentType: documentName,
        rentalId: Number.parseInt(rentalId),
        ...(carParameters && { carParameters })
      },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: async () => {
            setOpenDocFormDialogDocumentName(null);
            setRegenOpenTemplateName(null);
            await queryClient.invalidateQueries({
              queryKey: ['common', ...RENTAL_CAR_INFO_KEY, rentalCar?.car?.id]
            });
          }
        })
      }
    );

  const handleSignDocument = (signBase64: string) => {
    if (!selectedDocumentName) return;
    generateDocument(
      {
        rentalDocumentType: selectedDocumentName,
        rentalId: Number.parseInt(rentalId),
        files: [
          {
            filename: 'sign.png',
            data: signBase64,
            template: 'tenantSignature'
          }
        ]
      },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => {
            notifyOnSuccess('Dokument byl podepsán!');
            setSelectedDocumentName(null);
          },
          failed: () => {
            notifyOnError('Chyba při podepisování dokumentu!');
          }
        })
      }
    );
  };

  return (
    <PageLayout
      title="Detail zápůjčky"
      backPath={parentPath}
      {...props}
      extra={
        user?.role === Role.Dealer && (
          <Stack direction="row" gap={1} justifyContent="end">
            {!rentalCar?.isReturned && (
              <>
                {!isEmpty(rentalCar?.car) && rentalCar?.isBlocked ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenStartRentDialogId?.(Number.parseInt(rentalId))}
                  >
                    Zapůjčit vůz
                  </Button>
                ) : (
                  rentalCar?.isRunning && (
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={() => setRentFinishDialogId?.(Number.parseInt(rentalId))}
                      startIcon={<CheckCircle />}
                    >
                      Dokončit zápůjčku
                    </Button>
                  )
                )}
              </>
            )}
            <Tooltip
              title="Zrušit zápůjčku"
              placement="top"
              onClick={() => setRentRemoveDialogId?.(Number.parseInt(rentalId))}
            >
              <IconButton color="error">
                <Trash01 />
              </IconButton>
            </Tooltip>
          </Stack>
        )
      }
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, xl: 7 }}>
          <BoxWithHeader
            fullHeight
            title={
              <Stack direction="row" gap={1} alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                  Informace o zápůjčce
                </Typography>
                {rentalCar?.isReturned && (
                  <TransparentChip size="small" label="Vráceno" color="success" variant="filled" />
                )}
                {rentalCar?.isRunning && (
                  <TransparentChip size="small" label="Probíhající" color="info" variant="filled" />
                )}
                {rentalCar?.isBlocked && (
                  <TransparentChip size="small" label="Blokovaná" color="warning" variant="filled" />
                )}
                {rentalCar?.isExpired && (
                  <TransparentChip size="small" label="Neukončená" color="warning" variant="filled" />
                )}
              </Stack>
            }
          >
            <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
              <Stack gap={{ xs: 1, md: 2 }}>
                <Stack divider={<Divider flexItem />} sx={{ '& > *': { paddingY: 1.25 } }}>
                  <InfoRow label="Číslo zápůjčky">{rentalCar?.rentNumber}</InfoRow>
                  <InfoRow label="Číslo nehody">
                    <RouterLink to={PATHS.ACCIDENTS(rentalCar?.accident?.id)}>
                      <Link component="span">{rentalCar?.accident && rentalCar?.accident.contractNumber}</Link>
                    </RouterLink>
                  </InfoRow>
                  <InfoRow label="Denní nájemné">
                    {typeof rentalCar?.dailyRate === 'number' ? formatPrice(rentalCar.dailyRate) : rentalCar?.dailyRate}
                  </InfoRow>
                  <InfoRow label="Datum zápůjčky">
                    {format(new Date(rentalCar?.rentalDate ?? new Date()), 'dd.MM.yyyy')}
                  </InfoRow>
                  <InfoRow label="Datum vrácení">
                    {rentalCar?.returnDate && format(new Date(rentalCar?.returnDate ?? new Date()), 'dd.MM.yyyy')}
                  </InfoRow>
                  <InfoRow label="Automobil">
                    {[rentalCar?.car?.brand, rentalCar?.car?.model].filter(Boolean).join(' ')}
                  </InfoRow>
                  <InfoRow label="SPZ">{rentalCar?.car?.plateNumber}</InfoRow>
                </Stack>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Typography fontWeight="700">Poznámka k zápůjčce</Typography>
                  </Grid>
                  <Grid size={12}>
                    <Controller
                      control={control}
                      name="rental.note"
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          placeholder="Poznámka ..."
                          variant="outlined"
                          onChange={onChange}
                          value={value}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          multiline
                          minRows={3}
                          disabled
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </form>
          </BoxWithHeader>
        </Grid>
        <Grid size={{ xs: 12, xl: 5 }}>
          <BoxWithHeader title="Dokumenty k autopůjčovně" fullHeight>
            <Stack gap={2}>
              {[
                {
                  title: 'Smlouva',
                  template: RENT_FILES.CONTRACT,
                  generateIcon: null
                },
                {
                  title: 'Předávací protokol',
                  template: RENT_FILES.HANDOVER_PROTOCOL,
                  generateIcon: <FilePlus01 size="16px" />,
                  requireParameters: true
                },
                {
                  title: 'Přebírací protokol',
                  template: RENT_FILES.TAKEOVER_PROTOCOL,
                  generateIcon: <FilePlus01 size="16px" />,
                  requireParameters: true
                },
                {
                  title: 'Dodatek smlouvy',
                  template: RENT_FILES.AMENDMENT,
                  generateIcon: <UploadCloud01 size="16px" />
                }
              ].map(({ title, template, generateIcon, requireParameters }) => {
                const document = rentalCar?.documents?.find((doc) => doc.template === template);

                return (
                  <Stack
                    key={template}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, padding: 2, gap: 1.5 }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                      <LineClamp maxLines={1} fontWeight={600}>
                        {title}
                      </LineClamp>
                      {document?.path && document.isSigned && (
                        <TransparentChip size="small" label="Podepsáno" color="success" variant="filled" />
                      )}
                    </Stack>
                    {isLoadingData ? (
                      <CircularProgress size="16px" />
                    ) : document?.path ? (
                      <Stack alignItems="center" direction="row" justifyContent="space-between" width={1}>
                        <BasicDocumentValueItem
                          fileName={document?.filename ?? title}
                          url={document.path}
                          createdDate={document?.updatedDate}
                        />
                        {user?.role === Role.Dealer && (
                          <Stack direction="row">
                            {!document.isSigned && (
                              <Tooltip title="Podepsat dokument">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => setSelectedDocumentName(template)}
                                >
                                  <Pencil02 size="20px" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {document.isGenerable && (
                              <Tooltip title="Přegenerovat dokument">
                                <IconButton
                                  color="info"
                                  size="small"
                                  loading={isGeneratingDocument}
                                  onClick={() => {
                                    if (document.isSigned) {
                                      setRegenOpenTemplateName(template);
                                    } else {
                                      handleGenerateDocument(template);
                                    }
                                  }}
                                >
                                  <RefreshCCW04 size="20px" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        )}
                      </Stack>
                    ) : (
                      generateIcon &&
                      user?.role === Role.Dealer && (
                        <Box mt={2}>
                          <GenerateDocumentButton
                            generateIcon={generateIcon}
                            onClick={() => {
                              if (requireParameters) {
                                setOpenDocFormDialogDocumentName(template);
                              } else {
                                handleGenerateDocument(template);
                              }
                            }}
                            loading={isGeneratingDocument}
                            disabled={!document?.isGenerable}
                          />
                        </Box>
                      )
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </BoxWithHeader>
        </Grid>
      </Grid>

      <ConfirmRegenerateDocumentDialog
        setConfirmRegenOpen={() => setRegenOpenTemplateName(null)}
        confirmRegenOpen={!!regenOpenTemplateName}
        handleConfirmRegen={() => handleGenerateDocument(regenOpenTemplateName as RENT_FILES)}
        isGeneratingDocument={isGeneratingDocument}
      />

      <RentStartConfirmDialog
        open={!!openStartRentDialogId}
        onClose={() => setOpenStartRentDialogId(null)}
        onAccept={handleStartRent}
        isLoading={isStartingRent}
        rentalDate={rentalCar?.rentalDate}
        returnDate={rentalCar?.returnDate}
      />
      <RentFinishConfirmDialog
        open={!!rentFinishDialogId}
        onClose={() => setRentFinishDialogId(null)}
        onAccept={handleFinishRent}
        onDeny={() => setRentFinishDialogId(null)}
        isLoading={isTakingoverRent}
        carBranches={carRentalBranches}
        carStatuses={carRentalStatuses}
      />
      <RentRemoveConfirmDialog
        open={!!rentRemoveDialogId}
        onClose={() => setRentRemoveDialogId(null)}
        onAccept={() =>
          handleRemoveRent({
            onSuccess: () => {
              navigate(PATHS.CAR_RENTAL());
            }
          })
        }
        onDeny={() => setRentRemoveDialogId(null)}
        isLoading={isRemovingRent}
      />
      <SignatureDialog
        open={!!selectedDocumentName}
        onClose={() => {
          setSelectedDocumentName(null);
        }}
        onSave={async (signatureBase64) => handleSignDocument(signatureBase64)}
        loading={isGeneratingDocument}
      />
      <RentalCarInfoDialog
        onSubmit={(data) => handleGenerateDocument(openDocFormDialogDocumentName as RENT_FILES, data)}
        open={!!openDocFormDialogDocumentName}
        onClose={() => setOpenDocFormDialogDocumentName(null)}
        formData={rentalCarInfo?.carConditionParams}
        isLoading={isGeneratingDocument}
      />
    </PageLayout>
  );
};

export const RentalPage = (props: Omit<RentalPageContentProps, 'rentalId'>) => {
  const { rentalId } = useParams<{ rentalId: string }>();
  if (!rentalId) return null;
  return <RentalPageContent rentalId={rentalId} {...props} />;
};
